import type { Curve, Event, ParsedData } from '../../../../interfaces/toolData';
import type { WaveFileType } from '../../types';

/**
 * Parser for JAE Smart DM SYSTEM files
 * 
 * Handles both state changes (converted to curves) and true events:
 * - "Flow State Changed to [1]" -> Curve: "Flow State" with yValue=1
 * - "Config Software Connected" -> Event: "Config Events"
 */
export class SmartDmSystemParser {
  static parse(fileContent: string): ParsedData {
    const lines = fileContent.split('\n');
    
    // Validate file format
    if (!lines[0]?.trim().startsWith('SmartDM')) {
      throw new Error('Invalid JAE Smart DM SYSTEM file format');
    }
    
    if (!lines[1]?.trim().includes('SYSTEM Log')) {
      throw new Error('Not a JAE Smart DM SYSTEM file');
    }
    
    // Extract metadata from header
    const metadata = this.extractMetadata(lines);
    
    // Find the data header and data start
    let dataHeaderIndex = -1;
    let dataStartIndex = -1;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.includes('CLOCK_TICKS,RTC_TIME,STRING_MSG')) {
        dataHeaderIndex = i;
        dataStartIndex = i + 1;
        break;
      }
    }
    
    if (dataHeaderIndex === -1) {
      throw new Error('Could not find data header in JAE Smart DM SYSTEM file');
    }
    
    // Parse CSV data manually since Papa Parse is confused by the header format
    const headerLine = lines[dataHeaderIndex];
    const columnNames = headerLine.split(',').map(name => name.trim());
    
    const dataLines = lines.slice(dataStartIndex).filter(line => line.trim());
    const rawData: Record<string, string>[] = [];
    
    for (const line of dataLines) {
      const values = line.split(',');
      const record: Record<string, string> = {};
      
      columnNames.forEach((colName, index) => {
        if (index < values.length) {
          record[colName] = values[index]?.trim() || '';
        }
      });
      
      rawData.push(record);
    }
    
    const data = rawData;
    
    if (data.length === 0) {
      throw new Error('No valid data found in JAE Smart DM SYSTEM file');
    }
    
    // Parse messages and categorize into curves vs events
    const { curves, events } = this.parseSystemMessages(data);
    
    return {
      toolType: 'JAE',
      fileType: 'JAE_SMARTDM_SYSTEM' as WaveFileType,
      curves,
      events,
      metadata,
      dataPreview: {
        totalCount: data.length,
        first5: data.slice(0, 5)
      }
    };
  }
  
  private static extractMetadata(lines: string[]): Record<string, string> {
    const metadata: Record<string, string> = {};
    
    for (const line of lines) {
      const trimmed = line.trim();
      
      // Extract key-value pairs from header
      if (trimmed.includes(':') && !trimmed.includes('RTC_TIME') && !trimmed.includes('CLOCK_TICKS')) {
        const [key, ...valueParts] = trimmed.split(':');
        const value = valueParts.join(':').trim();
        
        if (key && value) {
          metadata[key.trim()] = value;
        }
      }
    }
    
    return metadata;
  }
  
  private static parseSystemMessages(data: Record<string, string>[]): { curves: Curve[], events: Event[] } {
    // Maps for aggregating data
    const stateChanges = new Map<string, Array<{ timestamp: Date, value: number }>>();
    const eventGroups = new Map<string, Array<{ timestamp: Date, message?: string }>>();
    
    for (const row of data) {
      const timestampStr = row['RTC_TIME'];
      const message = row['STRING_MSG'];
      
      if (!timestampStr || !message) continue;
      
      const timestamp = new Date(timestampStr);
      if (isNaN(timestamp.getTime())) continue;
      
      // Check if this is a state change message
      const stateMatch = message.match(/^(.*?)\s+State\s+Changed\s+to\s+\[([^\]]+)\]$/i);
      
      if (stateMatch) {
        // Handle state changes as curves
        const [, stateName, stateValueStr] = stateMatch;
        const stateValue = this.parseStateValue(stateValueStr);
        
        if (!stateChanges.has(stateName)) {
          stateChanges.set(stateName, []);
        }
        stateChanges.get(stateName)!.push({ timestamp, value: stateValue });
        
      } else {
        // Handle other messages as events
        const eventName = this.categorizeMessage(message);
        
        if (!eventGroups.has(eventName)) {
          eventGroups.set(eventName, []);
        }
        
        // Store message if it varies for this event type
        const shouldStoreMessage = this.messageVaries(eventName);
        eventGroups.get(eventName)!.push({
          timestamp,
          message: shouldStoreMessage ? message : undefined
        });
      }
    }
    
    // Convert state changes to curves
    const curves: Curve[] = [];
    for (const [stateName, dataPoints] of stateChanges) {
      // Sort by timestamp
      dataPoints.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      
      curves.push({
        name: stateName,
        xValues: dataPoints.map(dp => dp.timestamp),
        yValues: dataPoints.map(dp => dp.value),
        yUnit: 'state'
      });
    }
    
    // Convert event groups to events
    const events: Event[] = [];
    for (const [eventName, eventList] of eventGroups) {
      // Sort by timestamp
      eventList.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      
      const hasMessages = eventList.some(e => e.message !== undefined);
      
      events.push({
        name: eventName,
        xValues: eventList.map(e => e.timestamp),
        yValues: hasMessages ? eventList.map(e => e.message || '') : undefined
      });
    }
    
    return { curves, events };
  }
  
  private static parseStateValue(stateValueStr: string): number {
    // Handle various state value formats
    const trimmed = stateValueStr.trim();
    
    // Simple numeric values
    const numValue = parseFloat(trimmed);
    if (!isNaN(numValue)) {
      return numValue;
    }
    
    // Handle complex state strings like "0 0-0-0"
    if (trimmed.includes(' ')) {
      const firstValue = parseFloat(trimmed.split(' ')[0]);
      if (!isNaN(firstValue)) {
        return firstValue;
      }
    }
    
    // Default to 1 for any unrecognized state
    return 1;
  }
  
  private static categorizeMessage(message: string): string {
    const msg = message.toLowerCase();
    
    if (msg.includes('boot')) {
      return 'Boot Events';
    }
    
    if (msg.includes('config') && msg.includes('connected')) {
      return 'Config Connected';
    }
    
    if (msg.includes('config') && msg.includes('disconnected')) {
      return 'Config Disconnected';
    }
    
    if (msg.includes('datetime') && msg.includes('updated')) {
      return 'Time Sync Events';
    }
    
    if (msg.includes('download')) {
      return 'Download Events';
    }
    
    // Default catch-all
    return 'System Events';
  }
  
  private static messageVaries(eventName: string): boolean {
    // For some event types, the message provides useful detail
    // For others (like "Boot"), the message is always the same
    
    switch (eventName) {
      case 'Boot Events':
      case 'Config Connected':
      case 'Config Disconnected':
        return false; // Messages are uniform, no need to store
      
      case 'Time Sync Events':
      case 'Download Events':
      case 'System Events':
        return true; // Messages provide useful detail
      
      default:
        return true;
    }
  }
} 